import React, { useMemo, useState } from 'react';
import { X, AlertCircle, DollarSign, Slash, MinusCircle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';

// OverageDecisionDialog — Ops resolves an event flagged for overage
// review (spec §12). Pre-fills the charge amount based on
// overage_minutes at $200/hr rounded up to the next half-hour; Ops can
// override with a custom number or pick "credit_consumed" / "waived".

const RATE_PER_HOUR = 20_000;  // $200/hr in cents

function suggestedCents(minutes) {
  if (!minutes || minutes <= 0) return 0;
  const halfHours = Math.ceil(minutes / 30);
  return Math.round((halfHours / 2) * RATE_PER_HOUR);
}

export default function OverageDecisionDialog({ event, onClose, onDecided }) {
  const suggested = useMemo(() => suggestedCents(event.overageMinutes), [event.overageMinutes]);
  const [decision, setDecision] = useState('charged');
  const [customDollars, setCustomDollars] = useState(String((suggested / 100).toFixed(0)));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const body = { decision };
      if (decision === 'charged') {
        const d = parseFloat(customDollars);
        if (!Number.isFinite(d) || d < 0) throw new Error('Enter a valid amount.');
        body.amountCents = Math.round(d * 100);
      }
      const r = await api.post(`/api/admin/media/events/${event.id}/overage-decision`, body);
      onDecided?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Failed to record decision'));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Overage decision</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={event.eventTitle}>
              {event.eventTitle} · {Math.round(event.overageMinutes)} min over
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="text-[12px] text-gray-400">
            Suggested charge: <strong className="text-white">${(suggested / 100).toLocaleString()}</strong>
            <span className="text-gray-500"> ($200/hr, rounded to next 0.5 hr)</span>
          </div>

          <div className="space-y-1.5">
            <DecisionOption
              icon={DollarSign} label="Charge" sub="Adds to next invoice"
              active={decision === 'charged'} onClick={() => setDecision('charged')} />
            <DecisionOption
              icon={MinusCircle} label="Consume credit" sub="With the school's consent, one credit comes off"
              active={decision === 'credit_consumed'} onClick={() => setDecision('credit_consumed')} />
            <DecisionOption
              icon={Slash} label="Waive" sub="No charge, no credit consumed"
              active={decision === 'waived'} onClick={() => setDecision('waived')} />
          </div>

          {decision === 'charged' && (
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Charge amount</span>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">$</span>
                <input type="number" min="0" step="1" required value={customDollars}
                  onChange={(e) => setCustomDollars(e.target.value)}
                  className="w-32 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px]" />
              </div>
              <span className="text-[11px] text-gray-500 mt-1 block">
                Line item is added to the school's invoice queue. v1: Ops still needs to push it onto the next invoice
                manually via /admin/financial.
              </span>
            </label>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
              {submitting ? 'Recording…' : 'Record decision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DecisionOption({ icon: Icon, label, sub, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`block w-full text-left px-3 py-2.5 rounded-lg border ${
        active ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.08] text-white'
               : 'border-white/[0.06] text-gray-400 hover:text-white hover:border-white/[0.1]'}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${active ? 'text-[#B0FC38]' : 'text-gray-500'}`} />
        <span className="text-[13px]">{label}</span>
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5 pl-5">{sub}</div>
    </button>
  );
}
